import { type JSX, useCallback, useId, useMemo, useRef, useState } from 'react';

import { useRovingTabIndex } from '@/hooks/useRovingTabIndex';

import { ScrollTabsPanel } from './components/ScrollTabsPanel';
import { useScrollTabsObserver } from './hooks/useScrollTabsObserver';
import { useScrollToSection } from './hooks/useScrollToSection';
import { ScrollTabsContentFactory, ScrollTabsListFactory, ScrollTabsRootFactory } from './scrollTabs.factory';
import { type ScrollTabsProps } from './types/types';

export function ScrollTabs<T = unknown>({
  items,
  className = '',
  ariaLabel = 'Abas com scroll',
  variant = 'line',
  defaultActiveId,
  onActiveChange,
}: ScrollTabsProps<T>): JSX.Element {
  const [activeId, setActiveId] = useState<string | number>(() => {
    if (defaultActiveId !== undefined) return defaultActiveId;
    if (items.length === 0) return '';
    const firstItem = items[0];
    if (firstItem === undefined) return '';
    return firstItem.id;
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const activeIndex = useMemo(
    () => (items.length > 0 ? items.findIndex((item) => item.id === activeId) : -1),
    [items, activeId]
  );

  const { register, sectionRefs, isProgrammaticScroll } = useScrollTabsObserver({
    items,
    containerRef: scrollContainerRef,
    onChange: (id) => {
      if (id !== activeId) {
        setActiveId(id);
        onActiveChange?.(id);
      }
    },
    dataAttribute: 'data-scroll-tab-id',
    axis: 'y',
  });

  const { scrollToId } = useScrollToSection({
    containerRef: scrollContainerRef,
    sectionRefs,
    isProgrammaticScroll,
    axis: 'y',
  });

  useRovingTabIndex(tabsListRef);

  const scrollToItem = useCallback(
    (id: string | number) => {
      const item = items.find((item) => item.id === id);
      if (!item) return;

      setActiveId(id);
      onActiveChange?.(id);
      scrollToId(id);
    },
    [items, onActiveChange, scrollToId]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          if (activeIndex >= 0 && activeIndex < items.length - 1) {
            const nextIndex = activeIndex + 1;
            const nextItem = items[nextIndex];
            if (nextItem !== undefined) {
              scrollToItem(nextItem.id);
            }
          }
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          if (activeIndex > 0) {
            const prevIndex = activeIndex - 1;
            const prevItem = items[prevIndex];
            if (prevItem !== undefined) {
              scrollToItem(prevItem.id);
            }
          }
          break;
        case 'Home':
          event.preventDefault();
          if (items.length > 0) {
            const firstItem = items[0];
            if (firstItem !== undefined) {
              scrollToItem(firstItem.id);
            }
          }
          break;
        case 'End':
          event.preventDefault();
          if (items.length > 0) {
            const lastIndex = items.length - 1;
            const lastItem = items[lastIndex];
            if (lastItem !== undefined) {
              scrollToItem(lastItem.id);
            }
          }
          break;
        default:
          break;
      }
    },
    [activeIndex, items, scrollToItem]
  );

  if (items.length === 0) {
    return (
      <ScrollTabsRootFactory
        variant={variant}
        className={className}>
        <ScrollTabsListFactory
          ref={tabsListRef}
          role='tablist'
          aria-label={ariaLabel}
          id={listId}>
          <p>Nenhuma aba disponível</p>
        </ScrollTabsListFactory>
        <ScrollTabsContentFactory ref={scrollContainerRef}>
          <p>Nenhum conteúdo disponível</p>
        </ScrollTabsContentFactory>
      </ScrollTabsRootFactory>
    );
  }

  return (
    <ScrollTabsRootFactory
      variant={variant}
      className={className}>
      <ScrollTabsListFactory
        ref={tabsListRef}
        role='tablist'
        aria-label={ariaLabel}
        id={listId}
        onKeyDown={handleKeyDown}>
        {items.map((item, index) => {
          const isActive = item.id === activeId;

          return (
            <button
              key={item.id}
              data-roving='item'
              role='tab'
              id={`${listId}-tab-${index}`}
              aria-controls={`${listId}-panel-${index}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              type='button'
              onClick={() => scrollToItem(item.id)}>
              {item.label}
            </button>
          );
        })}
      </ScrollTabsListFactory>

      <ScrollTabsContentFactory ref={scrollContainerRef}>
        {items.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => register(item.id, el)}
            data-scroll-tab-id={item.id}
            role='tabpanel'
            id={`${listId}-panel-${index}`}
            aria-labelledby={`${listId}-tab-${index}`}
            style={{ scrollMarginTop: '20px', marginBottom: '3rem' }}>
            {item.content}
          </div>
        ))}
      </ScrollTabsContentFactory>
    </ScrollTabsRootFactory>
  );
}

ScrollTabs.Panel = ScrollTabsPanel;
